import * as React from 'react';
import { ProgressiveHydration, registerComponent } from '@avensia/excite';
import { Document, type StyleDecorators } from '@avensia/excite-contentful';
import { Block, contentWidthDecorator, edgeMarginsDecorator } from 'webapp/components/page-layout';
import { styled } from '@glitz/react';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { mapContentVerticalAlignment } from 'webapp/shared/content-alignment';
import { decorators } from 'webapp/html-decorators/fontSizeDecorator';
import { useLayout } from 'webapp/shared/use-layout';

export default registerComponent<NordicNest.Api.TinymceEditorialBlock>(TinymceEditorialBlock);

function TinymceEditorialBlock(props: NordicNest.Api.TinymceEditorialBlock) {
  const { hasLayoutBlockParent } = useLayout();
  const backgroundDecorator = React.useMemo(
    () =>
      styled(
        createMaxSmallViewportDecorator({
          padding: { x: t => t.spacing.m },
        }),
        {
          backgroundColor: props.backgroundColorHex,
          padding: { x: t => t.spacing.xl, y: t => t.spacing.s },
        },
      ),
    [props.backgroundColorHex],
  );

  const textColorDecorator = React.useMemo(
    () =>
      styled({
        color: props.textColorHex,
      }),
    [props.textColorHex],
  );

  const alignmentDecorator = React.useMemo(
    () =>
      styled({
        justifyContent: mapContentVerticalAlignment(props.contentVerticalAlignment),
      }),
    [props.contentVerticalAlignment],
  );

  return (
    <ProgressiveHydration>
      <FullWidthDiv css={Boolean(props.backgroundColorHex) && backgroundDecorator}>
        <Base
          id={props.id}
          css={styled(
            contentWidthDecorator(props.contentWidth),
            props.marginTop && marginTopDecorator,
            props.marginBottom && marginBottomDecorator,
            Boolean(props.textColorHex) && textColorDecorator,
            !hasLayoutBlockParent && edgeMarginsDecorator,
            props.noEdgeMargin && noEdgeMarginDecorator,
            alignmentDecorator,
          )}
        >
          <Document
            elements={props.content}
            decorators={props.scaleFont ? decorators : undefined}
            styleDecorators={props.contentMargin ? undefined : styles}
          />
        </Base>
      </FullWidthDiv>
    </ProgressiveHydration>
  );
}

const FullWidthDiv = styled.div({
  display: 'flex',
  width: '100%',
});

const Base = styled(
  Block,
  {
    display: 'flex',
    flexDirection: 'column',
  },
  createMaxSmallViewportDecorator({
    padding: { x: t => t.spacing.m },
  }),
);

const noEdgeMarginDecorator = styled(
  createMaxSmallViewportDecorator({
    padding: { x: 0 },
  }),
);

const marginTopDecorator = styled(createMaxSmallViewportDecorator({ marginTop: t => t.spacing.m }), {
  marginTop: t => t.spacing.xl,
});

const marginBottomDecorator = styled({ marginBottom: t => t.spacing.l });

const styles: StyleDecorators = {
  h1: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
  }),
  h2: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
  }),
  h3: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
  }),
  p: styled(createMinLargeViewportDecorator({ marginBottom: 0 }), {
    marginBottom: 0,
  }),
};
